import React, { useState, useEffect, useMemo } from 'react';
import { POIData } from './POIOverlay';

interface AssetFilterPanelProps {
  /** Full unfiltered POI list — used to derive layer names and counts. */
  pois: POIData[];
  /** Ref to the trigger button — used to right-align the dropdown. */
  anchorRef: React.RefObject<HTMLButtonElement>;
  /** Accept the current selection and dismiss the panel. */
  onOk: () => void;
  /** Dismiss the panel and clear assets from the map. */
  onClose: () => void;
  /** Which layer names are currently visible. Owned by the parent. */
  activeLayers: Set<string>;
  /** Called when the user toggles a single layer. */
  onToggleLayer: (name: string) => void;
  /** Called when the user wants to isolate a single layer. */
  onShowOnlyLayer: (name: string) => void;
  /** Called when the user clicks "Show all" / "Hide all". */
  onToggleAll: () => void;
}

const AssetFilterPanel: React.FC<AssetFilterPanelProps> = ({
  pois,
  anchorRef,
  onOk,
  onClose,
  activeLayers,
  onToggleLayer,
  onShowOnlyLayer,
  onToggleAll,
}) => {
  const [panelRight, setPanelRight] = useState<number | null>(null);

  // Anchor dropdown below button — same pattern as MeasureTool
  useEffect(() => {
    if (!anchorRef.current) return;
    const update = () => {
      const rect = anchorRef.current!.getBoundingClientRect();
      setPanelRight(window.innerWidth - rect.right);
    };
    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, [anchorRef]);

  // Unique layers with POI counts
  const uniqueLayers = useMemo(() =>
    Array.from(
      pois.reduce((map, p) => {
        const name = p.layerName ?? '(unlabelled)';
        map.set(name, (map.get(name) ?? 0) + 1);
        return map;
      }, new Map<string, number>())
    ).map(([name, count]) => ({ name, count })),
    [pois]
  );

  const allVisible = activeLayers.size === uniqueLayers.length;

  return (
    <div
      className="fixed z-[1002] w-64 rounded-2xl overflow-hidden"
      style={{
        top: 68,
        right: panelRight ?? 16,
        background: 'rgba(0,0,0,0.6)',
        backdropFilter: 'blur(12px)',
        WebkitBackdropFilter: 'blur(12px)',
        border: '1px solid rgba(255,255,255,0.12)',
        boxShadow: '0 8px 32px rgba(0,0,0,0.45)',
      }}
    >
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
        <span style={{ color: 'white', fontSize: 13, fontWeight: 600 }}>Layers</span>
        <button
          onClick={onToggleAll}
          style={{ fontSize: 11, color: 'rgba(255,255,255,0.45)', cursor: 'pointer', background: 'none', border: 'none' }}
        >
          {allVisible ? 'Hide all' : 'Show all'}
        </button>
      </div>

      {/* Layer rows */}
      <div className="py-2">
        {uniqueLayers.map(({ name, count }) => (
          <div
            key={name}
            className="grid grid-cols-[minmax(0,1fr)_44px_28px] items-center gap-3 px-4 py-2 hover:bg-white/5"
            style={{ userSelect: 'none' }}
          >
            <label
              className="flex items-center gap-3 min-w-0 flex-1 cursor-pointer"
              style={{ userSelect: 'none' }}
            >
              <input
                type="checkbox"
                checked={activeLayers.has(name)}
                onChange={() => onToggleLayer(name)}
                style={{ accentColor: '#0CB4E4', cursor: 'pointer' }}
              />
              <span style={{ flex: 1, fontSize: 12, color: 'rgba(255,255,255,0.85)' }}>{name}</span>
            </label>
            <button
              type="button"
              onClick={() => onShowOnlyLayer(name)}
              style={{
                fontSize: 11,
                color: 'rgba(255,255,255,0.55)',
                cursor: 'pointer',
                background: 'none',
                border: 'none',
                padding: 0,
                textAlign: 'right',
              }}
            >
              Only
            </button>
            <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.35)', textAlign: 'right' }}>{count}</span>
          </div>
        ))}
      </div>

      <div className="flex gap-2 px-4 pb-4 pt-2 border-t border-white/10" style={{ pointerEvents: 'auto' }}>
        <button
          type="button"
          onClick={onOk}
          className="flex-1 py-2 rounded-xl text-xs font-semibold transition-all"
          style={{
            background: 'rgba(255,255,255,0.06)',
            border: '1px solid rgba(255,255,255,0.12)',
            color: 'rgba(255,255,255,0.55)',
            cursor: 'pointer',
          }}
        >
          Ok
        </button>
        <button
          type="button"
          onClick={onClose}
          className="flex-1 py-2 rounded-xl text-xs font-semibold transition-all"
          style={{
            background: 'rgba(255,255,255,0.06)',
            border: '1px solid rgba(255,255,255,0.12)',
            color: 'rgba(255,255,255,0.55)',
            cursor: 'pointer',
          }}
        >
          Close
        </button>
      </div>
    </div>
  );
};

export default AssetFilterPanel;
